// 🌿 API Craft Back Office - Dashboard Page
import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useModels } from '../hooks/useModels';
import { usePageTitle, PAGE_TITLES } from '../hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { models, isLoading, error } = useModels();
  const navigate = useNavigate();
  
  // Set page title
  usePageTitle(PAGE_TITLES.DASHBOARD);
  
  // This component only renders when user is authenticated, so user should exist

  return (
    <Layout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="stats-card bg-gradient-to-br from-mint-50 to-emerald-100 animation-slide-down">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-forest-800 mb-2">
                Welcome back, {user?.email?.split('@')[0] || 'User'}! 👋
              </h1>
              <p className="text-sage-700 text-lg">
                Manage your dynamic APIs and data models from this central dashboard.
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-24 h-24 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-strong">
                <span className="text-4xl text-white">🌿</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animation-slide-up">
          <div className="stats-card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-soft">
                <span className="text-white text-xl">📊</span>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-sage-600">Total Models</h3>
                <p className="text-3xl font-bold text-forest-800">
                  {isLoading ? '...' : (models?.length || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-sage-400 to-mint-500 rounded-xl flex items-center justify-center shadow-soft">
                <span className="text-white text-xl">🔗</span>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-sage-600">API Endpoints</h3>
                <p className="text-3xl font-bold text-forest-800">
                  {isLoading ? '...' : (models?.length || 0) * 5}
                </p>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-mint-500 rounded-xl flex items-center justify-center shadow-soft">
                <span className="text-white text-xl">⚡</span>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-sage-600">Status</h3>
                <p className="text-lg font-bold text-emerald-600">Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-xl font-bold text-forest-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/models')}
              className="btn-primary text-left flex items-center justify-between"
            >
              <div className="flex items-center">
                <span className="mr-3 text-lg">📊</span>
                <span>Manage Models</span>
              </div>
              <span>→</span>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="btn-secondary text-left flex items-center justify-between"
            >
              <div className="flex items-center">
                <span className="mr-3 text-lg">👤</span>
                <span>View Profile</span>
              </div>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Error or Loading State */}
        {error && (
          <div className="alert-error">
            <span className="text-red-500 mr-2">⚠️</span>
            {error}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <svg className="animate-spin h-8 w-8 text-mint-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sage-600">Loading dashboard...</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
